/**
 * ExtractionTierTrail — per-file extraction-tier ladder for a case.
 *
 * Reads GET /api/v1/documents/extraction-tiers/{caseId} (READ-ONLY; never runs
 * extraction) and renders the 4-tick trail per doc, dispatched by doc-type group
 * so the ticks never lie (a policy form's schema is DI frontmatter, not
 * parsed_data). Tri-state per tier: true → present ✓ (neutral), false →
 * expected-but-missing ✕ (the one red), null → not-applicable – (faint dash).
 *
 * Standalone component (Data-Specialist per-case extraction review). Contract +
 * matrix: docs/plans/2026-09-05-fl5-53-extraction-tier-matrix.md.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../utils/api';
import { Loader2, AlertTriangle, ChevronRight, ChevronDown, RefreshCw, Upload, Trash2, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface TierFile {
  file_id: string;
  filename: string | null;
  document_type: string | null;
  canonical: string;
  group: string;
  doctype: boolean;
  schema: boolean | null;
  summary: boolean | null;
  verbatim: boolean | null;
  // Increment A: id of the OPEN UNREADABLE_DOCUMENT issue this file is the affected_item
  // of (Phase 2a), or null. Present => the "Replace document" action threads it as
  // resolves_issue_id so a successful replacement auto-clears the flag (2c).
  issue_id?: string | null;
  // Increment C-2 ("land + flag"): a client uploaded a replacement for this row's issue via
  // the per-document magic link. The issue stays REQUESTED (NOT auto-resolved) so a
  // specialist verifies the client's file before clearing it. replacement_upload_id points
  // at the newest landed upload the specialist reviews/resolves against; replacement_filename
  // lets them see WHICH file they are accepting; replacement_count > 1 flags a multi-file batch.
  replacement_received?: boolean;
  replacement_upload_id?: string | null;
  replacement_filename?: string | null;
  replacement_count?: number;
}

interface CountsByGroup { A: number; B: number; C: number; D: number; }

interface ExtractionTiersResponse {
  claim_id: string;
  total: number;
  counts_by_group: CountsByGroup;
  duplicates: number;
  files: TierFile[];
}

interface ExtractionTierTrailProps {
  caseId: string;
}

// Per-group descriptor for the expanded detail header (the parser/source family).
const GROUP_DESC: Record<string, string> = {
  A: 'FINANCIAL PARSER',
  B: 'NARRATIVE / DI',
  C: 'VISION',
  D: 'EDGE',
};

export default function ExtractionTierTrail({ caseId }: ExtractionTierTrailProps) {
  const { user } = useUser();
  const [data, setData] = useState<ExtractionTiersResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [failuresOnly, setFailuresOnly] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Increment A: per-file action state + a single hidden file input reused for "Replace".
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingReplace = useRef<{ fileId: string; issueId: string } | null>(null);

  const setBusyFor = (fileId: string, on: boolean) => {
    setBusy(prev => {
      const next = new Set(prev);
      if (on) next.add(fileId); else next.delete(fileId);
      return next;
    });
  };
  // Increment B: two-step inline confirm for the destructive Delete action.
  const [confirmDelete, setConfirmDelete] = useState<Set<string>>(new Set());
  const setConfirmDeleteFor = (fileId: string, on: boolean) => {
    setConfirmDelete(prev => {
      const next = new Set(prev);
      if (on) next.add(fileId); else next.delete(fileId);
      return next;
    });
  };
  // Increment C-1: which row's "Request from client" email form is open, + the entered
  // recipient. One at a time (opening another closes this one).
  const [requestEmailFor, setRequestEmailFor] = useState<string | null>(null);
  const [requestEmailVal, setRequestEmailVal] = useState<string>('');

  // `silent` skips the loading spinner so a post-action refetch doesn't flash the whole
  // list (and the hidden file input) out of the DOM.
  const fetchInventory = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const result = await api.get<ExtractionTiersResponse>(
        `/api/v1/documents/extraction-tiers/${caseId}`,
        { showErrorToast: false }
      );
      setData(result);
    } catch (err: any) {
      if (!silent) setError(err?.message || 'Failed to load extraction tiers');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) fetchInventory();
  }, [caseId]);

  const toggleExpanded = (fileId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  // Increment A — Reprocess: re-run extraction on this one file (existing endpoint).
  // Extraction runs in the background, so we refetch shortly after to pick up new tiers.
  const handleReprocess = async (fileId: string) => {
    setBusyFor(fileId, true);
    try {
      // /reprocess is SYNCHRONOUS — it does the work inline and returns the outcome.
      // Read it and toast the truth (a discarded response + a timer would lie on the
      // 0-match / failed / skipped paths). It filters plain claim_id, so a draft_id-only
      // upload can legitimately match nothing (total_files === 0).
      const r = await api.post<{
        total_files?: number;
        results?: Array<{ status?: string; error?: string | null }>;
      }>(
        '/api/v1/upload-savefirst/reprocess',
        { upload_ids: [fileId], claim_id: caseId },
        { showErrorToast: false }
      );
      if (!r?.total_files) {
        toast.error('No matching upload record for this case to reprocess.');
      } else {
        const res = r.results?.[0];
        if (res?.status === 'success') {
          toast.success('Reprocessed — extraction re-run complete.');
        } else if (res?.status === 'skipped') {
          toast.error('Skipped — this file type cannot be reprocessed here.');
        } else {
          toast.error(res?.error || 'Reprocess failed.');
        }
      }
      await fetchInventory(true);
    } catch (err: any) {
      toast.error(err?.message || 'Reprocess failed');
    } finally {
      setBusyFor(fileId, false);
    }
  };

  // Increment A — Replace: open the file picker for this row; the actual upload happens
  // in handleReplaceFile once a file is chosen. issueId threads into resolves_issue_id so
  // a successful parse auto-resolves the UNREADABLE_DOCUMENT issue (2c).
  const triggerReplace = (fileId: string, issueId: string) => {
    pendingReplace.current = { fileId, issueId };
    fileInputRef.current?.click();
  };

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const target = pendingReplace.current;
    e.target.value = ''; // reset so the same filename can be re-picked later
    if (!file || !target) return;
    setBusyFor(target.fileId, true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('claim_id', caseId);
      fd.append('uploader_type', 'specialist');
      fd.append('resolves_issue_id', target.issueId);
      await api.upload('/api/v1/upload-savefirst/unified', fd, { showErrorToast: false });
      // Parsing (and the 2c auto-resolve) happens in the background, so the flag may still
      // be present on the immediate refetch — don't overpromise.
      toast.success('Uploaded — the flag clears once the replacement parses (refresh to check).');
      await fetchInventory(true);
      // A large PDF may still be parsing; re-check once more shortly.
      setTimeout(() => fetchInventory(true), 8000);
    } catch (err: any) {
      toast.error(err?.message || 'Replacement upload failed');
    } finally {
      setBusyFor(target.fileId, false);
      // Only clear if this settle still owns the pending slot — another row's dialog may
      // have overwritten it while this upload was in flight (dialogs aren't modal to JS).
      if (pendingReplace.current === target) pendingReplace.current = null;
    }
  };

  // Increment B — Delete: the audited server endpoint (DELETE /documents/{id}) is
  // org-gated, cascade-cleans (document_intelligence + merchant envelope), invalidates the
  // canonical cache (so analysis recomputes without the doc — no orphans), and writes an
  // audit_chain row. Gated behind an inline two-step confirm because it's destructive.
  const handleDelete = async (fileId: string) => {
    setBusyFor(fileId, true);
    try {
      await api.delete(`/api/v1/documents/${fileId}`, undefined, { showErrorToast: false });
      toast.success('Document deleted.');
      setConfirmDeleteFor(fileId, false);
      await fetchInventory(true);
    } catch (err: any) {
      toast.error(err?.message || 'Delete failed');
    } finally {
      setBusyFor(fileId, false);
    }
  };

  // Increment C-1 — Request from client: emails the client an on-demand per-document magic
  // link to upload a readable copy. The recipient is staff-entered (real cases carry no
  // client email on file). The client's eventual upload lands + is flagged for verify.
  const openRequestForm = (fileId: string) => {
    setRequestEmailFor(fileId);
    setRequestEmailVal('');
  };
  const handleSendRequest = async (fileId: string) => {
    const email = requestEmailVal.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid recipient email.');
      return;
    }
    setBusyFor(fileId, true);
    try {
      const r = await api.post<{ email_sent?: boolean; email_error?: string | null }>(
        `/api/v1/documents/${fileId}/request-replacement`,
        { recipient_email: email },
        { showErrorToast: false }
      );
      if (r?.email_sent) {
        toast.success(`Request emailed to ${email}.`);
      } else {
        // The link is minted + the issue moved to requested even if the mail service is off.
        toast.success(`Request created for ${email}${r?.email_error ? ` (email not sent: ${r.email_error})` : ''}.`);
      }
      setRequestEmailFor(null);
      setRequestEmailVal('');
      await fetchInventory(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send request');
    } finally {
      setBusyFor(fileId, false);
    }
  };

  // Increment C-2 — Resolve (client provided): the client uploaded a replacement via the
  // per-document magic link, which LANDED + was flagged (replacement_received) but was
  // deliberately NOT auto-resolved (unauthenticated legal evidence must be verified by a
  // human first). The specialist reviews the client's file, then clears the issue here.
  // POST /issues/{issue_id}/resolve records resolved_by_document = the client's upload and
  // walks REQUESTED → RESOLVED (the same positive outcome as a specialist Replace, not a
  // dismiss). user_id is a recorded-actor query param; auth is the bearer (current_user).
  const handleResolveClientProvided = async (
    fileId: string,
    issueId: string,
    replacementUploadId: string,
  ) => {
    const userId = user?.id || user?.primaryEmailAddress?.emailAddress || 'unknown-user';
    setBusyFor(fileId, true);
    try {
      await api.post(
        `/api/v1/issues/${issueId}/resolve?user_id=${encodeURIComponent(userId)}`,
        { document_id: replacementUploadId, confidence: 'high' },
        { showErrorToast: false },
      );
      toast.success('Resolved — the client-provided replacement was accepted.');
      await fetchInventory(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resolve issue');
    } finally {
      setBusyFor(fileId, false);
    }
  };

  const sortedFiles = useMemo(() => {
    if (!data) return [];
    let files = data.files;
    if (failuresOnly) {
      files = files.filter(
        f => f.doctype === false || f.schema === false || f.summary === false || f.verbatim === false
      );
    }
    return [...files].sort((a, b) => {
      const aFail = [a.doctype, a.schema, a.summary, a.verbatim].some(v => v === false) ? 1 : 0;
      const bFail = [b.doctype, b.schema, b.summary, b.verbatim].some(v => v === false) ? 1 : 0;
      if (aFail !== bFail) return bFail - aFail;
      return (a.filename || '').localeCompare(b.filename || '');
    });
  }, [data, failuresOnly]);

  const renderTick = (state: boolean | null) => {
    if (state === true) {
      return <span className="font-semibold" style={{ color: 'var(--color-verified)' }} aria-hidden>✓</span>;
    }
    if (state === false) {
      return <span className="font-semibold" style={{ color: 'var(--color-blocker)' }} aria-hidden>✕</span>;
    }
    return <span className="font-normal" style={{ color: 'var(--color-text-ghost)' }} aria-hidden>–</span>;
  };

  const getDetailState = (field: 'doctype' | 'schema' | 'summary' | 'verbatim', state: boolean | null, group: string) => {
    if (field === 'doctype') {
      if (state === true) return { word: 'CLASSIFIED', color: 'var(--color-text-primary)' };
      if (state === false) return { word: 'UNCLASSIFIED', color: 'var(--color-blocker)' };
      return { word: 'NOT APPLICABLE', color: 'var(--color-text-ghost)' };
    }
    if (field === 'summary' && group === 'C' && state === true) {
      return { word: 'DESCRIBED', color: 'var(--color-text-primary)' };
    }
    if (state === true) return { word: 'EXTRACTED', color: 'var(--color-text-primary)' };
    if (state === false) return { word: 'MISSING', color: 'var(--color-blocker)' };
    return { word: 'NOT APPLICABLE', color: 'var(--color-text-ghost)' };
  };

  const getSourceLabel = (
    field: 'schema' | 'summary' | 'verbatim',
    state: boolean | null,
    group: string
  ): string | null => {
    switch (group) {
      case 'A':
        if (field === 'schema') return state === true ? 'parsed_data' : state === null ? 'Group A financials produce no narrative summary' : null;
        if (field === 'summary') return state === null ? 'Group A financials produce no narrative summary' : null;
        if (field === 'verbatim') return 'extracted_text';
        break;
      case 'B':
        if (field === 'schema') return 'document_intelligence.frontmatter';
        if (field === 'summary') return 'document_intelligence.body';
        if (field === 'verbatim') return 'extracted_text';
        break;
      case 'C':
        if (field === 'schema') return state === null ? 'photos are vision-described, not parsed' : null;
        if (field === 'summary') return 'document_intelligence.body (vision)';
        if (field === 'verbatim') return state === null ? 'source is a vision description' : null;
        break;
      case 'D':
        if (field === 'schema') return state === true ? 'parsed_data' : state === null ? 'edge document — no schema expected' : null;
        if (field === 'summary') return null;
        if (field === 'verbatim') return 'extracted_text';
        break;
    }
    return null;
  };

  const srLabel = (file: TierFile) => {
    const d = file.doctype === true ? 'classified' : file.doctype === false ? 'unclassified' : 'not applicable';
    const s = file.schema === true ? 'present' : file.schema === false ? 'missing' : 'not applicable';
    const u = file.summary === true ? 'present' : file.summary === false ? 'missing' : 'not applicable';
    const v = file.verbatim === true ? 'present' : file.verbatim === false ? 'missing' : 'not applicable';
    return `${file.filename || 'unnamed'}: doctype ${d}; schema ${s}; summary ${u}; verbatim ${v}`;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)] text-[var(--color-text-muted)] font-mono text-xs">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading document inventory…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--color-blocker)' }}>
          <AlertTriangle className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider font-mono">Error</span>
        </div>
        <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
        <button
          onClick={() => fetchInventory()}
          className="px-3 py-1 text-xs uppercase tracking-wider border rounded-[2px] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
          style={{ borderColor: 'var(--color-border)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.total === 0 || data.files.length === 0) {
    return (
      <div className="p-4 border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)] font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>
        No documents uploaded for this case.
      </div>
    );
  }

  return (
    <div className="border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)]">
      {/* Increment A: single hidden input reused by every row's "Replace document" action. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.csv,.xlsx,.xls"
        className="hidden"
        onChange={handleReplaceFile}
      />
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
        <span className="text-xs uppercase tracking-wider font-mono" style={{ color: 'var(--color-text-muted)' }}>
          Document Inventory
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setFailuresOnly(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors ${
              failuresOnly
                ? 'bg-[var(--color-blocker-tint)] border-[var(--color-blocker-tint-bd)] text-[var(--color-blocker)]'
                : 'border-[var(--color-border-sub)] text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]'
            }`}
          >
            <span aria-hidden>✕</span>
            Failures Only
          </button>
          <div className="flex items-center gap-2 text-xs font-mono" style={{ color: 'var(--color-text-secondary)' }}>
            <span>{data.total} documents</span>
            {data.duplicates > 0 && (
              <span style={{ color: 'var(--color-text-dim)' }}>· {data.duplicates} duplicates hidden</span>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border-sub)' }}>
        <span className="font-mono text-[10px] tracking-wider" style={{ color: 'var(--color-text-dim)' }}>
          DOC doctype · SCH schema · SUM summary · VER word-for-word — ✓ extracted · ✕ missing · – n/a
        </span>
      </div>

      <div className="px-4 py-1.5 flex items-center justify-end" style={{ color: 'var(--color-text-dim)' }}>
        <div className="grid grid-cols-4 gap-2 w-[104px] text-center font-mono text-[9px] uppercase tracking-wider">
          <span>DOC</span>
          <span>SCH</span>
          <span>SUM</span>
          <span>VER</span>
        </div>
        <div className="w-6" />
      </div>

      <div className="divide-y" style={{ borderColor: 'var(--color-border-sub)' }}>
        {sortedFiles.map(file => {
          const isExpanded = expanded.has(file.file_id);
          return (
            <div key={file.file_id} className="bg-[var(--color-bg-surface)]">
              <button
                onClick={() => toggleExpanded(file.file_id)}
                className="w-full flex items-center gap-4 px-4 py-2 hover:bg-[var(--color-bg-hover)] transition-colors text-left"
              >
                <span className="sr-only">{srLabel(file)}</span>
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  <span className="text-[13px] truncate" style={{ color: 'var(--color-text-secondary)' }} title={file.filename || undefined}>
                    {file.filename || 'unnamed'}
                  </span>
                  <span
                    className="font-mono text-[11px] uppercase truncate flex-shrink-0"
                    style={{ color: 'var(--color-text-muted)', maxWidth: '190px' }}
                  >
                    {file.canonical}
                  </span>
                  {file.issue_id && (
                    <span
                      className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] flex-shrink-0"
                      style={{ background: 'var(--color-blocker-tint)', color: 'var(--color-blocker)', border: '1px solid var(--color-blocker-tint-bd)' }}
                    >
                      Unreadable
                    </span>
                  )}
                  {/* Increment C-2: the client uploaded a replacement via the magic link — it
                      landed but is NOT auto-resolved; a specialist must verify + resolve it. */}
                  {file.replacement_received && (
                    <span
                      className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] flex-shrink-0"
                      style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                    >
                      Replacement — verify
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="grid grid-cols-4 gap-2 w-[104px] text-center text-xs">
                    {renderTick(file.doctype)}
                    {renderTick(file.schema)}
                    {renderTick(file.summary)}
                    {renderTick(file.verbatim)}
                  </div>
                  <div className="w-6 flex justify-center">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                    ) : (
                      <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                    )}
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div className="bg-[var(--color-bg-elevated)] border-t px-4 py-3 space-y-2" style={{ borderColor: 'var(--color-hairline)' }}>
                  <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                    <span className="truncate">EXTRACTION TIERS — {file.file_id}</span>
                    <span className="flex-shrink-0 pl-3">GROUP {file.group} · {GROUP_DESC[file.group] || 'EDGE'}</span>
                  </div>
                  <div className="font-mono text-[11px]" style={{ color: 'var(--color-text-dim)' }}>
                    TYPE  {file.canonical} (canonical) · raw: {file.document_type || '—'}
                  </div>
                  {/* Increment C-2: explain the "Replacement — verify" flag so the specialist
                      knows a client sent a file via the magic link and it awaits their review. */}
                  {file.replacement_received && (
                    <div
                      className="flex items-start gap-1.5 font-mono text-[11px] px-2 py-1.5 rounded-[2px]"
                      style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                      <span>
                        A client uploaded a replacement via the magic link
                        {file.replacement_filename ? (
                          <> — <span className="font-semibold">{file.replacement_filename}</span></>
                        ) : null}
                        {file.replacement_count && file.replacement_count > 1
                          ? ` (${file.replacement_count} files received)`
                          : null}
                        . Review it, then
                        <span className="font-semibold"> Resolve (client provided)</span> to clear this issue.
                      </span>
                    </div>
                  )}
                  {(['doctype', 'schema', 'summary', 'verbatim'] as const).map(field => {
                    const state = file[field];
                    const meta = getDetailState(field, state, file.group);
                    const source = field === 'doctype' ? null : getSourceLabel(field, state, file.group);
                    return (
                      <div key={field} className="flex items-baseline gap-3 font-mono text-[12px]">
                        <span className="w-16 text-[11px] uppercase" style={{ color: 'var(--color-text-ghost)' }}>
                          {field}
                        </span>
                        <span className="w-28 inline-flex items-center gap-1.5">
                          {renderTick(state)}
                          <span style={{ color: meta.color }}>{meta.word}</span>
                        </span>
                        {source && (
                          <span className="flex-1 truncate" style={{ color: 'var(--color-text-muted)' }}>
                            {source}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {/* Increment A/B: per-doc remediation actions. Replace (auto-resolves the
                      linked UNREADABLE_DOCUMENT issue via 2c) shows only on issue rows.
                      Reprocess is gated to Group A (the financial family /reprocess is designed
                      for): on a non-financial PDF that endpoint re-classifies by filename and
                      DEFAULTS to bank_statement, corrupting the doc_type — never offer it on
                      B/C/D. Delete (any doc — remove duplicates/superseded) uses the audited,
                      org-gated, cascade-cleaning endpoint, behind a two-step confirm. */}
                  <div className="flex items-center gap-2 pt-2 mt-1 border-t" style={{ borderColor: 'var(--color-hairline)' }}>
                    {/* Increment C-2: a client uploaded a replacement via the magic link. It
                        landed + was flagged but NOT auto-resolved — the specialist reviews it,
                        then accepts it here (walks the issue REQUESTED → RESOLVED). */}
                    {file.replacement_received && file.issue_id && file.replacement_upload_id && (
                      <button
                        onClick={() => handleResolveClientProvided(file.file_id, file.issue_id!, file.replacement_upload_id!)}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50"
                        style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                      >
                        {busy.has(file.file_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Resolve (client provided)
                      </button>
                    )}
                    {file.issue_id && (
                      <button
                        onClick={() => triggerReplace(file.file_id, file.issue_id!)}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50"
                        style={{ background: 'var(--color-blocker-tint)', borderColor: 'var(--color-blocker-tint-bd)', color: 'var(--color-blocker)' }}
                      >
                        {busy.has(file.file_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        Replace document
                      </button>
                    )}
                    {file.issue_id && (
                      <button
                        onClick={() => openRequestForm(file.file_id)}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]"
                        style={{ borderColor: 'var(--color-border-sub)' }}
                      >
                        <Mail className="w-3 h-3" />
                        Request from client
                      </button>
                    )}
                    {file.group === 'A' && (
                      <button
                        onClick={() => handleReprocess(file.file_id)}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]"
                        style={{ borderColor: 'var(--color-border-sub)' }}
                      >
                        {busy.has(file.file_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Reprocess
                      </button>
                    )}
                    {confirmDelete.has(file.file_id) ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider font-mono" style={{ color: 'var(--color-blocker)' }}>
                          Delete this document?
                        </span>
                        <button
                          onClick={() => handleDelete(file.file_id)}
                          disabled={busy.has(file.file_id)}
                          className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono rounded-[2px] transition-colors disabled:opacity-50"
                          style={{ background: 'var(--color-blocker)', color: 'var(--color-on-brand)' }}
                        >
                          {busy.has(file.file_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDeleteFor(file.file_id, false)}
                          disabled={busy.has(file.file_id)}
                          className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]"
                          style={{ borderColor: 'var(--color-border-sub)' }}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteFor(file.file_id, true)}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50 text-[var(--color-text-dim)] hover:text-[var(--color-blocker)]"
                        style={{ borderColor: 'var(--color-border-sub)' }}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    )}
                  </div>
                  {requestEmailFor === file.file_id && (
                    <div className="flex items-center flex-wrap gap-2 pt-2 mt-1 border-t" style={{ borderColor: 'var(--color-hairline)' }}>
                      <span className="text-[10px] uppercase tracking-wider font-mono" style={{ color: 'var(--color-text-dim)' }}>
                        Email a request to:
                      </span>
                      <input
                        type="email"
                        value={requestEmailVal}
                        onChange={(e) => setRequestEmailVal(e.target.value)}
                        placeholder="client@email.com"
                        className="text-[11px] px-2 py-1 rounded-[2px] outline-none font-mono"
                        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-sub)', color: 'var(--color-text-primary)', minWidth: '210px' }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendRequest(file.file_id); }}
                      />
                      <button
                        onClick={() => handleSendRequest(file.file_id)}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-mono rounded-[2px] transition-colors disabled:opacity-50"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                      >
                        {busy.has(file.file_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                        Send request
                      </button>
                      <button
                        onClick={() => { setRequestEmailFor(null); setRequestEmailVal(''); }}
                        disabled={busy.has(file.file_id)}
                        className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-mono border rounded-[2px] transition-colors disabled:opacity-50 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)]"
                        style={{ borderColor: 'var(--color-border-sub)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
